import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '../../../data/atoms/utilsAtoms';
import { useFeaturedPlayListQuery } from '../../../service';
import { getFeaturedPlayList } from '../../../redux/featuredPlayListSlice';
import MobileViewSearch from '../../../component/MobileViewSearch';
import { AllPlayList } from '../../../component/common/AllPlayList';
import PlaylistHeader from './components/PlaylistHeader';
import { FeaturedPlaylistModal } from './components/FeaturedPlaylistModal';
import PlaylistGrid from './components/PlaylistGrid';
import checkApiStatus from '../../../component/SubscripStatus';
import { useVideo } from './hooks/useVideo';
import videoApi from '../../../api/videoApi';

const hasActiveSubscription = userData =>
	Boolean(userData?.subscriptionId || userData?.user?.subscriptionId || userData?.user?.subscription);

export const FeaturedPlayList = () => {
	const dispatch = useDispatch();
	const userData = useAtomValue(userInfoAtom);

	// State
	const [subscribtionStatus, setSubscribtionStatus] = useState(() => hasActiveSubscription(userData));
	const [editPlaylistID, setEditPlaylistID] = useState('');
	const [showModal, setModalShow] = useState(false);
	const [index, setIndex] = useState(0);
	const [recommendedVideos, setRecommendedVideos] = useState([]);
	const [savedVideos, setSavedVideos] = useState([]);
	const [refreshSavedVideos, setRefreshSavedVideos] = useState(null);

	// API queries
	const resFeaturedPlayList = useFeaturedPlayListQuery({ token: userData.token });

	// Select playlist data from Redux
	const playlists = useSelector(state => state.featuredPlayListState.featuredPlayList);
	const currentPlaylist = playlists && playlists[index];

	const { toggleSaveVideo } = useVideo(userData.token);

	useEffect(() => {
		const getSubscription = async () => {
			const subscribtionStatus = await checkApiStatus(userData.token);
			setSubscribtionStatus(subscribtionStatus || hasActiveSubscription(userData));
		};
		if (!userData.token) return;
		getSubscription();
	}, [userData]);

	// Fetch saved videos
	useEffect(() => {
		if (userData?.user?.userId ?? userData?.user?._id) {
			const fetchSavedVideos = async () => {
				try {
					const response = await videoApi.getAllSavedVideoIds(userData?.user?.userId ?? userData?.user?._id);
					setSavedVideos(response.data.savedVideos);
				} catch (error) {
					console.error('Error fetching saved videos:', error);
				}
			};

			fetchSavedVideos();
		}
	}, [userData.token, refreshSavedVideos]);

	// Update Redux state when API data changes
	useEffect(() => {
		if (resFeaturedPlayList?.isSuccess && resFeaturedPlayList?.data?.existingVideos) {
			const playlists = resFeaturedPlayList.data.existingVideos;
			dispatch(getFeaturedPlayList(playlists));

			// Extract recommended videos from all playlists
			if (Array.isArray(playlists) && playlists.length > 0) {
				const allVideos = playlists
					.map(playlist => playlist.videos)
					.flat()
					.filter(video => video !== undefined && video !== null);

				setRecommendedVideos(allVideos);
			}
		} else {
			dispatch(getFeaturedPlayList([]));
			setRecommendedVideos([]);
		}
	}, [resFeaturedPlayList, dispatch]);

	// Modal handlers
	const handleCloseModal = () => {
		setModalShow(false);
		resFeaturedPlayList.refetch({ userId: userData.user?.userId });
	};

	const handleEditPlaylist = playlist => {
		setEditPlaylistID(playlist._id);
		setModalShow(true);
	};

	const shuffleArray = array => {
		const newArray = [...array];
		for (let i = newArray.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[newArray[i], newArray[j]] = [newArray[j], newArray[i]];
		}
		return newArray;
	};

	const handleShuffle = id => {
		if (
			resFeaturedPlayList?.data?.existingVideos &&
			Array.isArray(resFeaturedPlayList?.data?.existingVideos) &&
			resFeaturedPlayList?.data?.existingVideos?.length > 0
		) {
			const shuffledArrayOfObjects = resFeaturedPlayList?.data?.existingVideos?.map(obj => {
				return {
					...obj,
					videos: obj?._id === id ? shuffleArray(obj?.videos) : obj?.videos
				};
			});
			if (shuffledArrayOfObjects && Array.isArray(shuffledArrayOfObjects) && shuffledArrayOfObjects?.length > 0) {
				dispatch(getFeaturedPlayList(shuffledArrayOfObjects));
			}
		}
	};

	return (
		<>
			{/* Mobile Search */}
			<div className="row m-0 update c-mobile_honm-serch">
				<MobileViewSearch />
			</div>

			<div>
				<div className="tab-content">
					<div id="menu2" className="tab-pane fade active show">
						{/* Playlist selector */}
						<AllPlayList
							title="FEATURED PLAYLISTS"
							data={playlists}
							page="Featured"
							currentIndex={index}
							setCurrentIndex={setIndex}
							playlistType={'featured-playlist'}
							subscribtionStatus={subscribtionStatus}
						/>

						{/* Edit Modal */}
						<FeaturedPlaylistModal
							showModal={showModal}
							setModalShow={setModalShow}
							handleClose={handleCloseModal}
							editPlaylistID={editPlaylistID}
						/>

						{/* Playlist content */}
						<div
							className="c-video c-video_feature-box update updated__play__card__list"
							style={{ backgroundColor: 'var(--dark)' }}
						>
							{currentPlaylist ? (
								<div className="row m-0 mt-2 position-relative" key={index}>
									<div className="position-absolute start-0 w-100" style={{ top: '-100px' }} id={index}></div>

									{/* Playlist header with controls */}
									<PlaylistHeader
										playlist={currentPlaylist}
										isAdmin={userData.role === 'admin'}
										onEdit={handleEditPlaylist}
										onDelete={playlistId => reqDelete(playlistId)}
										onShuffle={playlistId => handleShuffle(playlistId)}
									/>

									{/* Videos grid */}
									<PlaylistGrid
										playlist={currentPlaylist}
										hasSubscription={subscribtionStatus}
										savedVideos={savedVideos}
										onSaveUnsave={(videoId, video) => toggleSaveVideo(videoId, video)}
									/>
								</div>
							) : (
								<div className="text-center py-4">No Featured playlists exist</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
